/**
 * ObjectiveDashboardPage
 *
 * Página rica de una campaña. Muestra TODO en una sola vista:
 *
 *   1. Hero header (emoji, nombre, estado, modo, acciones)
 *   2. KPI strip (alcance, conversiones, ingresos, tasa conversión, costo)
 *   3. Performance chart — evolución de los últimos 30 días
 *   4. Embudo de conversión — enviado → entregado → leído → respondido → convertido
 *   5. "Cómo se armó" — modo Manual/Auto + rationale del preset
 *   6. Configuración completa — segmento + plantilla con preview + cadencia
 *   7. Historial de ejecuciones — runs pasados (tabla)
 *   8. Zona peligrosa
 *
 * En paralelo carga: la campaña + el segmento referenciado + la plantilla
 * referenciada, para mostrar previews reales en lugar de IDs.
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
    ArrowLeft,
    Users,
    Target,
    DollarSign,
    Percent,
    Pause,
    Play,
    Pencil,
    Copy,
    Trash2,
    Loader2,
    AlertCircle,
    Clock,
    FileText,
    Repeat,
    Send,
    CheckCheck,
    Eye,
    Reply,
    Sparkles,
    BrainCircuit,
    Edit3,
    Activity,
    Mail,
    MessageCircle,
    Zap,
    GitMerge,
    CheckCircle,
    Bell,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
    FunnelChart,
    Funnel,
    Cell,
} from "recharts";
import { EVENT_CATALOG } from "../event-catalog";
import { Button } from "../../../shared/components/ui/Button";
import { ConfirmDangerModal } from "../../../shared/components/ui/ConfirmDangerModal";
import { toast } from "../../../shared/hooks/useToast";
import { objectivesApi } from "../api";
import type { ObjectiveRun } from "../api";
import { segmentsApi } from "../../segments/api";
import { whatsappApi } from "../../whatsapp/api";
import type { Objective, CampaignChannel, Trigger, AIObservation } from "../types";
import type { Segment } from "../../segments/types";
import type { WhatsAppTemplate } from "../../whatsapp/types";
import { PRESET_BY_KEY, TARGET_METRICS } from "../types";
import ObjectiveStatusBadge from "../components/ObjectiveStatusBadge";

const DAY_LABELS: Record<number, string> = {
    1: "Lun",
    2: "Mar",
    3: "Mié",
    4: "Jue",
    5: "Vie",
    6: "Sáb",
    7: "Dom",
};

/**
 * Genera datos de performance para el chart. Si la campaña tiene runs
 * reales en metrics.runsCount > 0, usa los números reales agregados.
 * Si no, genera datos plausibles para que la pantalla no se vea vacía.
 */
function buildPerformanceData(objective: Objective) {
    const isReal = objective.metrics.runsCount > 0;
    const days: { day: string; enviados: number; conversiones: number; ingresos: number }[] = [];

    for (let i = 13; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const label = `${d.getDate()}/${d.getMonth() + 1}`;

        if (isReal) {
            // Distribuir las métricas en los últimos 14 días (placeholder hasta
            // que el worker real persista runs diarios)
            const avg = objective.metrics.peopleReached / 14;
            const convAvg = objective.metrics.conversions / 14;
            const revAvg = objective.metrics.revenueGeneratedUSD / 14;
            days.push({
                day: label,
                enviados: Math.round(avg + (Math.random() - 0.5) * avg * 0.3),
                conversiones: Math.round(convAvg + (Math.random() - 0.5) * convAvg * 0.4),
                ingresos: Math.round(revAvg + (Math.random() - 0.5) * revAvg * 0.4),
            });
        } else {
            // Datos demo (sin runs todavía)
            days.push({
                day: label,
                enviados: 0,
                conversiones: 0,
                ingresos: 0,
            });
        }
    }
    return { days, isReal };
}

export function ObjectiveDashboardPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [objective, setObjective] = useState<Objective | null>(null);
    const [segment, setSegment] = useState<Segment | null>(null);
    const [template, setTemplate] = useState<WhatsAppTemplate | null>(null);
    const [runs, setRuns] = useState<ObjectiveRun[]>([]);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    /* ─── Reload helper ─── */
    const reload = async () => {
        if (!id) return;
        const res = await objectivesApi.getById(id);
        if (!res.success) {
            setLoadError(res.error || "No se pudo cargar la campaña");
            return;
        }
        setObjective(res.data);

        // Cargar segmento y plantilla en paralelo para mostrar previews ricas
        const tasks: Promise<unknown>[] = [];
        if (res.data.segmentId) {
            tasks.push(
                segmentsApi
                    .get(res.data.segmentId, res.data.brandId)
                    .then((r) => r.success && setSegment(r.data))
                    .catch(() => null),
            );
        }
        if (res.data.templateId) {
            tasks.push(
                whatsappApi
                    .getTemplateById(res.data.templateId)
                    .then((r) => r.success && setTemplate(r.data))
                    .catch(() => null),
            );
        }
        await Promise.all(tasks);
        // Historial de ejecuciones (carga en paralelo, no bloquea)
        objectivesApi.getRuns(id).then((r) => {
            if (r.success) setRuns(r.data);
        });
    };

    useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            await reload();
            setLoading(false);
        })();
        // location.key changes even when navigating to the same route, so inbox
        // notifications that link back to this page trigger a fresh fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.key]);

    /* ─── Actions ─── */
    const handlePause = async () => {
        if (!id) return;
        setBusy(true);
        setActionError(null);
        const res = await objectivesApi.pause(id);
        if (!res.success) setActionError(res.error || "Error al pausar");
        await reload();
        setBusy(false);
    };
    const handleActivate = async () => {
        if (!id) return;
        setBusy(true);
        setActionError(null);
        const res = await objectivesApi.activate(id);
        if (!res.success) setActionError(res.error || "Error al activar");
        await reload();
        setBusy(false);
    };
    const handleDuplicate = async () => {
        if (!id) return;
        setBusy(true);
        setActionError(null);
        const res = await objectivesApi.duplicate(id);
        setBusy(false);
        if (res.success && res.data?.id) {
            toast.success("Campaña duplicada — configurando la copia");
            navigate(`/objectives/${res.data.id}/setup`);
        } else {
            const msg = res.error || "Error al duplicar";
            setActionError(msg);
            toast.error(msg);
        }
    };
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const handleDelete = () => {
        if (!id || !objective) return;
        setConfirmingDelete(true);
    };
    const confirmDelete = async () => {
        if (!id) return;
        setBusy(true);
        setActionError(null);
        const res = await objectivesApi.delete(id);
        setBusy(false);
        if (res.success) {
            setConfirmingDelete(false);
            toast.success("Campaña eliminada");
            navigate("/objectives");
        } else {
            const msg = res.error || "Error al borrar";
            setActionError(msg);
            toast.error(msg);
            setConfirmingDelete(false);
        }
    };

    /* ─── Derived data ─── */
    const presetMeta = useMemo(
        () => (objective ? PRESET_BY_KEY[objective.preset] : null),
        [objective],
    );

    const conversionRate = useMemo(() => {
        if (!objective || objective.metrics.peopleReached === 0) return 0;
        return (
            (objective.metrics.conversions / objective.metrics.peopleReached) *
            100
        );
    }, [objective]);

    const performanceData = useMemo(
        () => (objective ? buildPerformanceData(objective) : null),
        [objective],
    );

    /** Embudo: alcance → conversiones, derivado de los metrics */
    const funnelSteps = useMemo(() => {
        if (!objective) return [];
        const m = objective.metrics;
        const sent = m.peopleReached || 0;
        // Asumimos rates por defecto si no hay data; el backend pisará esto
        // cuando llegue el webhook de Meta.
        const delivered = Math.round(sent * 0.97);
        const read = Math.round(sent * 0.78);
        const replied = Math.round(sent * 0.15);
        const converted = m.conversions || 0;
        return [
            { label: "Enviados",     value: sent,      icon: Send,       fill: "#94a3b8" },
            { label: "Entregados",   value: delivered, icon: CheckCheck, fill: "#3b82f6" },
            { label: "Leídos",       value: read,      icon: Eye,        fill: "#6366f1" },
            { label: "Respondieron", value: replied,   icon: Reply,      fill: "#8b5cf6" },
            { label: "Convirtieron", value: converted, icon: Target,     fill: "#10b981" },
        ];
    }, [objective]);

    /** Data shaped for recharts FunnelChart */
    const funnelData = useMemo(
        () => funnelSteps.map((s) => ({ value: s.value, name: s.label, fill: s.fill })),
        [funnelSteps],
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex items-center gap-3 text-slate-500">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Cargando…</span>
                </div>
            </div>
        );
    }

    if (loadError || !objective) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="text-center max-w-md">
                    <p className="text-red-700 font-medium mb-3">
                        {loadError || "Campaña no encontrada"}
                    </p>
                    <Link
                        to="/objectives"
                        className="text-violet-600 text-sm hover:underline"
                    >
                        Volver a campañas
                    </Link>
                </div>
            </div>
        );
    }

    const isActive = objective.status === "active";
    const isPaused = objective.status === "paused";
    const isDraft = objective.status === "draft";
    const hasMetrics = objective.metrics.runsCount > 0;

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6">
                {/* Breadcrumb */}
                <Link
                    to="/objectives"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a campañas
                </Link>

                {/* ── 1. Hero header ── */}
                <div
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6"
                    style={{ background: presetMeta?.bgGradient }}
                >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                            <div className="text-5xl leading-none flex-shrink-0">
                                {objective.emoji}
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                    <ObjectiveStatusBadge
                                        status={objective.status}
                                        size="md"
                                    />
                                    {objective.setupMode === "auto" && (
                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 uppercase tracking-wider flex items-center gap-1">
                                            <BrainCircuit size={9} />
                                            Modo IA
                                        </span>
                                    )}
                                    {objective.setupMode === "manual" && (
                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wider flex items-center gap-1">
                                            <Edit3 size={9} />
                                            Manual
                                        </span>
                                    )}
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/70 text-slate-600 uppercase tracking-wider">
                                        {presetMeta?.shortDescription}
                                    </span>
                                </div>
                                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                                    {objective.name}
                                </h1>
                                {objective.metrics.lastRunAt && (
                                    <p className="text-xs text-slate-500 mt-1.5">
                                        Último envío:{" "}
                                        {new Date(
                                            objective.metrics.lastRunAt,
                                        ).toLocaleString("es-AR", {
                                            day: "numeric",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                            {isActive && (
                                <Button
                                    variant="secondary"
                                    onClick={handlePause}
                                    disabled={busy}
                                >
                                    {busy ? (
                                        <Loader2 size={15} className="animate-spin" />
                                    ) : (
                                        <Pause size={15} />
                                    )}
                                    Pausar
                                </Button>
                            )}
                            {(isPaused || isDraft) && (
                                <Button
                                    variant="primary"
                                    onClick={handleActivate}
                                    disabled={busy}
                                >
                                    {busy ? (
                                        <Loader2 size={15} className="animate-spin" />
                                    ) : (
                                        <Play size={15} />
                                    )}
                                    {isDraft ? "Activar" : "Reanudar"}
                                </Button>
                            )}
                            <Link to={`/objectives/${id}/setup/manual`}>
                                <Button variant="secondary">
                                    <Pencil size={15} />
                                    Editar
                                </Button>
                            </Link>
                            <Button
                                variant="secondary"
                                onClick={handleDuplicate}
                                disabled={busy}
                            >
                                <Copy size={15} />
                                Duplicar
                            </Button>
                        </div>
                    </div>
                </div>

                {actionError && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
                        <AlertCircle size={18} />
                        <span className="text-sm font-medium">{actionError}</span>
                    </div>
                )}

                {/* ── 2. KPI strip ── */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <KPICard
                        label="Alcance"
                        value={objective.metrics.peopleReached.toLocaleString("es-AR")}
                        icon={Users}
                        iconClass="text-blue-600"
                        bgClass="bg-blue-100"
                    />
                    <KPICard
                        label="Conversiones"
                        value={objective.metrics.conversions.toLocaleString("es-AR")}
                        icon={Target}
                        iconClass="text-emerald-600"
                        bgClass="bg-emerald-100"
                    />
                    <KPICard
                        label="Ingresos"
                        value={`$${objective.metrics.revenueGeneratedUSD.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`}
                        icon={DollarSign}
                        iconClass="text-green-600"
                        bgClass="bg-green-100"
                    />
                    <KPICard
                        label="Tasa conversión"
                        value={`${conversionRate.toFixed(1)}%`}
                        icon={Percent}
                        iconClass="text-violet-600"
                        bgClass="bg-violet-100"
                    />
                    <KPICard
                        label="Envíos totales"
                        value={objective.metrics.runsCount.toLocaleString("es-AR")}
                        icon={Activity}
                        iconClass="text-slate-600"
                        bgClass="bg-slate-100"
                    />
                </div>

                {/* ── 3. Performance chart + 4. Embudo ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Chart */}
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-semibold text-slate-900">
                                    Performance · últimos 14 días
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Mensajes enviados, conversiones e ingresos
                                </p>
                            </div>
                            {!hasMetrics && (
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 px-2 py-1 rounded-full">
                                        Sin datos todavía
                                    </span>
                                    {import.meta.env.VITE_MOCK_MODE === "true" && (
                                        <button
                                            onClick={async () => {
                                                if (!id) return;
                                                setBusy(true);
                                                await objectivesApi.seedMetrics(id);
                                                await reload();
                                                setBusy(false);
                                                toast.success("Datos demo generados");
                                            }}
                                            disabled={busy}
                                            className="text-[10px] text-violet-700 bg-violet-50 border border-violet-100 px-2 py-1 rounded-full hover:bg-violet-100 transition-colors disabled:opacity-50"
                                        >
                                            {busy ? "Generando…" : "✨ Generar datos demo"}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {hasMetrics && performanceData ? (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={performanceData.days}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#e2e8f0"
                                        />
                                        <XAxis
                                            dataKey="day"
                                            stroke="#94a3b8"
                                            fontSize={11}
                                        />
                                        <YAxis
                                            stroke="#94a3b8"
                                            fontSize={11}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: 12,
                                                border: "1px solid #e2e8f0",
                                                fontSize: 12,
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: 11,
                                                paddingTop: 8,
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="enviados"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="conversiones"
                                            stroke="#10b981"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="ingresos"
                                            stroke="#8b5cf6"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <EmptyChart />
                        )}
                    </div>

                    {/* Embudo — recharts FunnelChart (H12) */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col">
                        <h3 className="font-semibold text-slate-900 mb-1">
                            Embudo de conversión
                        </h3>
                        <p className="text-xs text-slate-500 mb-3">
                            Recorrido del mensaje al ingreso
                        </p>

                        {hasMetrics ? (
                            <>
                                <div className="h-48">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <FunnelChart>
                                            <Tooltip
                                                formatter={(val) => [
                                                    Number(val).toLocaleString("es-AR"),
                                                    "personas",
                                                ]}
                                                contentStyle={{
                                                    borderRadius: 8,
                                                    fontSize: 11,
                                                    border: "1px solid #e2e8f0",
                                                }}
                                            />
                                            <Funnel
                                                dataKey="value"
                                                data={funnelData}
                                                isAnimationActive
                                            >
                                                {funnelData.map((entry, i) => (
                                                    <Cell
                                                        key={`cell-${i}`}
                                                        fill={entry.fill}
                                                    />
                                                ))}
                                            </Funnel>
                                        </FunnelChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Legend with drop % */}
                                <div className="mt-3 space-y-1.5">
                                    {funnelSteps.map((step, i) => {
                                        const prev = i > 0 ? funnelSteps[i - 1].value : null;
                                        const dropPct =
                                            prev && prev > 0
                                                ? ((prev - step.value) / prev) * 100
                                                : 0;
                                        return (
                                            <div
                                                key={step.label}
                                                className="flex items-center justify-between text-xs"
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span
                                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                                        style={{ background: step.fill }}
                                                    />
                                                    <span className="text-slate-600 font-medium">
                                                        {step.label}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono font-bold text-slate-900">
                                                        {step.value.toLocaleString("es-AR")}
                                                    </span>
                                                    {i > 0 && dropPct > 0 && (
                                                        <span className="text-[10px] text-red-400 font-mono">
                                                            -{dropPct.toFixed(0)}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 rounded-xl min-h-[200px]">
                                <Activity
                                    size={28}
                                    className="text-slate-300 mb-2"
                                />
                                <p className="text-sm text-slate-500 font-medium">
                                    Sin datos todavía
                                </p>
                                <p className="text-xs text-slate-400 mt-1 text-center px-4">
                                    El embudo se llena con el primer envío
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── 5. Cómo se armó ── */}
                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border border-violet-100 p-5">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                            <Sparkles size={18} className="text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1">
                                Cómo se armó esta campaña
                            </p>
                            <p className="text-sm text-slate-700 leading-relaxed">
                                {objective.preset === "custom" && objective.customGoal
                                    ? objective.customGoal
                                    : presetMeta?.longDescription}
                            </p>

                            {/* Métricas monitoreadas — para TODOS los presets */}
                            {(() => {
                                // Prioridad: customMetrics (si custom) → trackedMetrics del preset
                                const monitoredMetrics =
                                    objective.preset === "custom"
                                        ? objective.customMetrics || []
                                        : presetMeta?.trackedMetrics || [];

                                if (monitoredMetrics.length === 0) return null;

                                const label =
                                    objective.preset === "custom"
                                        ? "Métricas a levantar (elegidas por vos)"
                                        : "KPIs que la IA monitorea";

                                return (
                                    <div className="mt-3 pt-3 border-t border-violet-200/50">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-2">
                                            {label}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {monitoredMetrics.map((m) => {
                                                const meta = TARGET_METRICS.find(
                                                    (t) => t.key === m,
                                                );
                                                if (!meta) return null;
                                                return (
                                                    <span
                                                        key={m}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-violet-700 border border-violet-200 text-[11px] font-semibold"
                                                        title={meta.description}
                                                    >
                                                        <span>{meta.emoji}</span>
                                                        {meta.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-600">
                                <span>
                                    Configurado en modo{" "}
                                    <strong className="text-slate-900">
                                        {objective.setupMode === "auto"
                                            ? "IA Autónoma"
                                            : objective.setupMode === "manual"
                                                ? "Manual"
                                                : "Sin definir"}
                                    </strong>
                                </span>
                                <span className="text-slate-300">·</span>
                                <span>
                                    Creado el{" "}
                                    {new Date(objective.createdAt).toLocaleDateString(
                                        "es-AR",
                                        { day: "numeric", month: "long", year: "numeric" },
                                    )}
                                </span>
                                <span className="text-slate-300">·</span>
                                <span>
                                    Última edición{" "}
                                    {new Date(objective.updatedAt).toLocaleDateString(
                                        "es-AR",
                                        { day: "numeric", month: "long" },
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── 6. Configuración completa ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Audiencia */}
                    <ConfigCard icon={Users} title="Audiencia">
                        {segment ? (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="font-semibold text-slate-900">
                                        {segment.name || "Sin nombre"}
                                    </p>
                                    <span className="text-sm font-bold text-violet-600 font-mono">
                                        {(segment.lastKnownCount || 0).toLocaleString("es-AR")}
                                    </span>
                                </div>
                                {segment.conversationSummary && (
                                    <p className="text-xs text-slate-500 italic leading-relaxed">
                                        "{segment.conversationSummary}"
                                    </p>
                                )}
                                <p className="text-[10px] text-slate-400">
                                    usuarios coinciden con los criterios
                                </p>
                            </div>
                        ) : objective.segmentId ? (
                            <p className="text-sm text-slate-400 italic">
                                Cargando segmento…
                            </p>
                        ) : (
                            <p className="text-sm text-slate-400 italic">
                                Sin segmento asignado
                            </p>
                        )}
                        <Link
                            to={`/objectives/${id}/setup/manual`}
                            className="text-xs text-violet-600 hover:underline mt-3 inline-block"
                        >
                            Cambiar segmento →
                        </Link>
                    </ConfigCard>

                    {/* Plantilla */}
                    <ConfigCard icon={FileText} title="Plantilla WhatsApp">
                        {template ? (
                            <div className="space-y-2">
                                <p className="font-mono text-sm font-semibold text-slate-900 truncate">
                                    {template.name}
                                </p>
                                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed border border-slate-100 max-h-32 overflow-y-auto">
                                    {template.body}
                                </div>
                                <p className="text-[10px] text-slate-400">
                                    {template.variableCount}{" "}
                                    {template.variableCount === 1
                                        ? "variable"
                                        : "variables"}{" "}
                                    · ${template.estimatedCostUSD.toFixed(4)} / conversación
                                </p>
                            </div>
                        ) : objective.templateId ? (
                            <p className="text-sm text-slate-400 italic">
                                Cargando plantilla…
                            </p>
                        ) : (
                            <p className="text-sm text-slate-400 italic">
                                Sin plantilla asignada
                            </p>
                        )}
                        <Link
                            to={`/objectives/${id}/setup/manual`}
                            className="text-xs text-violet-600 hover:underline mt-3 inline-block"
                        >
                            Cambiar plantilla →
                        </Link>
                    </ConfigCard>

                    {/* Cadencia (full width) */}
                    <ConfigCard
                        icon={Clock}
                        title="Horario y frecuencia"
                        className="lg:col-span-2"
                    >
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-1.5">
                                <span className="text-slate-500 text-xs">Ventana:</span>
                                <span className="text-sm font-semibold text-slate-900 font-mono">
                                    {objective.cadence.sendWindow.start} –{" "}
                                    {objective.cadence.sendWindow.end}
                                </span>
                            </div>
                            <span className="text-slate-300">·</span>
                            {objective.cadence.kind === "recurring" && (
                                <div className="flex items-center gap-1.5">
                                    <Repeat size={14} className="text-slate-400" />
                                    <span className="text-sm text-slate-700">
                                        {(objective.cadence.daysOfWeek || [])
                                            .map((d) => DAY_LABELS[d])
                                            .join(" · ")}
                                    </span>
                                </div>
                            )}
                            {objective.cadence.kind === "one_shot" &&
                                objective.cadence.scheduledFor && (
                                    <span className="text-sm text-slate-700">
                                        Una vez:{" "}
                                        {new Date(
                                            objective.cadence.scheduledFor,
                                        ).toLocaleString("es-AR", {
                                            day: "numeric",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                )}
                        </div>
                    </ConfigCard>
                </div>

                {/* ── 7. Canales y Triggers (H11) ── */}
                {(objective.channels?.length ||
                    objective.triggers?.items?.length) ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Canales activos */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Zap size={15} className="text-slate-400" />
                                <h3 className="font-semibold text-slate-900 text-sm">
                                    Canales activos
                                </h3>
                            </div>
                            {objective.channels?.length ? (
                                <div className="flex flex-wrap gap-2">
                                    {objective.channels.map((ch) => (
                                        <ChannelChip key={ch} channel={ch} />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 italic">
                                    Sin canales configurados aún
                                </p>
                            )}
                        </div>

                        {/* Triggers */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <GitMerge size={15} className="text-slate-400" />
                                <h3 className="font-semibold text-slate-900 text-sm">
                                    Triggers
                                </h3>
                                {objective.triggers && (
                                    <span
                                        className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                            objective.triggers.logic === "AND"
                                                ? "bg-violet-50 text-violet-700 border-violet-200"
                                                : "bg-amber-50 text-amber-700 border-amber-200"
                                        }`}
                                    >
                                        {objective.triggers.logic === "AND"
                                            ? "Todos deben cumplirse"
                                            : "Cualquiera dispara"}
                                    </span>
                                )}
                            </div>
                            {objective.triggers?.items?.length ? (
                                <div className="space-y-2">
                                    {objective.triggers.items.map((t) => (
                                        <TriggerRow key={t.id} trigger={t} />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 italic">
                                    Sin triggers configurados
                                </p>
                            )}
                        </div>
                    </div>
                ) : null}

                {/* ── 8. Revisión de la IA (H11) — solo si existe aiReview ── */}
                {objective.aiReview && (
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <BrainCircuit size={15} className="text-violet-500" />
                            <h3 className="font-semibold text-slate-900 text-sm">
                                Historial de revisión IA
                            </h3>
                            <span className="text-xs text-slate-400 font-mono">
                                · Iteración #{objective.aiReview.iteration}
                            </span>
                            {objective.aiReview.verdict === "approved" && (
                                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                                    <CheckCircle size={11} /> Aprobada
                                </span>
                            )}
                            {objective.aiReview.verdict === "needs_changes" && (
                                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                                    <AlertCircle size={11} /> Requiere cambios
                                </span>
                            )}
                        </div>

                        {objective.aiReview.approverNote && (
                            <div className="mb-4 flex items-start gap-2 p-3 bg-violet-50 rounded-xl border border-violet-100">
                                <Sparkles
                                    size={14}
                                    className="text-violet-500 flex-shrink-0 mt-0.5"
                                />
                                <p className="text-sm text-violet-800 italic leading-relaxed">
                                    "{objective.aiReview.approverNote}"
                                </p>
                            </div>
                        )}

                        {objective.aiReview.observations.length > 0 ? (
                            <div className="space-y-2">
                                {objective.aiReview.observations.map((obs, i) => (
                                    <AIObservationRow key={i} obs={obs} />
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-400 italic">
                                Sin observaciones — todo en orden ✓
                            </p>
                        )}

                        {objective.aiReview.lastReviewedAt && (
                            <p className="text-[10px] text-slate-400 mt-4">
                                Revisado el{" "}
                                {new Date(
                                    objective.aiReview.lastReviewedAt,
                                ).toLocaleString("es-AR", {
                                    day: "numeric",
                                    month: "short",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </p>
                        )}
                    </div>
                )}

                {/* ── 9. Historial de ejecuciones (placeholder) ── */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-semibold text-slate-900">
                                Historial de ejecuciones
                            </h3>
                            <p className="text-xs text-slate-500">
                                Cada vez que la campaña dispara un envío queda
                                registrado acá
                            </p>
                        </div>
                        <span className="text-xs font-mono text-slate-400">
                            {objective.metrics.runsCount} envío
                            {objective.metrics.runsCount === 1 ? "" : "s"}
                        </span>
                    </div>

                    {hasMetrics && runs.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[560px] text-left text-sm border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wider">
                                        <th className="pb-2 pr-4 font-semibold">Fecha</th>
                                        <th className="pb-2 pr-4 font-semibold text-right">Enviados</th>
                                        <th className="pb-2 pr-4 font-semibold text-right">Entregados</th>
                                        <th className="pb-2 pr-4 font-semibold text-right">Conversiones</th>
                                        <th className="pb-2 font-semibold text-right">Ingresos</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {runs.map((run) => (
                                        <tr key={run.id} className="hover:bg-slate-50/60 transition-colors">
                                            <td className="py-2.5 pr-4 text-slate-600 text-xs tabular-nums">
                                                {new Date(run.runAt).toLocaleString("es-AR", {
                                                    day: "numeric",
                                                    month: "short",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </td>
                                            <td className="py-2.5 pr-4 text-slate-700 font-mono text-xs text-right tabular-nums">
                                                {run.recipientsCount.toLocaleString("es-AR")}
                                            </td>
                                            <td className="py-2.5 pr-4 text-slate-700 font-mono text-xs text-right tabular-nums">
                                                <span className="text-emerald-600">
                                                    {run.deliveredCount.toLocaleString("es-AR")}
                                                </span>
                                                <span className="text-slate-400 ml-1 text-[10px]">
                                                    ({Math.round((run.deliveredCount / run.recipientsCount) * 100)}%)
                                                </span>
                                            </td>
                                            <td className="py-2.5 pr-4 text-slate-700 font-mono text-xs text-right tabular-nums">
                                                {run.conversions.toLocaleString("es-AR")}
                                            </td>
                                            <td className="py-2.5 font-mono text-xs text-right tabular-nums text-violet-700 font-semibold">
                                                ${run.revenueUSD.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : hasMetrics ? (
                        <div className="text-center py-6">
                            <Loader2 size={16} className="animate-spin text-slate-300 mx-auto" />
                        </div>
                    ) : (
                        <div className="text-center py-10 bg-slate-50 rounded-xl">
                            <Activity
                                size={28}
                                className="text-slate-300 mx-auto mb-2"
                            />
                            <p className="text-sm text-slate-500 font-medium">
                                Todavía no se ejecutó ningún envío
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                                {isActive
                                    ? `Esperando a que sea entre las ${objective.cadence.sendWindow.start} y las ${objective.cadence.sendWindow.end}`
                                    : "Activá la campaña para que empiece a enviar"}
                            </p>
                        </div>
                    )}
                </div>

                {/* ── 8. Danger zone ── */}
                <div className="border-t border-slate-200 pt-4 mt-8">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Zona peligrosa
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Borrar la campaña es irreversible y se pierden las
                                métricas.
                            </p>
                        </div>
                        <button
                            onClick={handleDelete}
                            disabled={busy || isActive}
                            className="px-4 py-2 rounded-lg text-xs font-semibold bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                            title={
                                isActive
                                    ? "Pausá la campaña antes de borrar"
                                    : "Borrar campaña"
                            }
                        >
                            <Trash2 size={13} />
                            Borrar campaña
                        </button>
                    </div>
                </div>
            </div>

            {confirmingDelete && objective && (
                <ConfirmDangerModal
                    title="Borrar campaña"
                    description={`¿Borrar la campaña "${objective.name}"? Esta acción no se puede deshacer.`}
                    onConfirm={confirmDelete}
                    onCancel={() => setConfirmingDelete(false)}
                    busy={busy}
                />
            )}
        </div>
    );
}

/* ─── Sub-components ─── */

interface KPICardProps {
    label: string;
    value: string;
    icon: typeof Users;
    iconClass: string;
    bgClass: string;
}

function KPICard({ label, value, icon: Icon, iconClass, bgClass }: KPICardProps) {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-2">
                <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center ${bgClass}`}
                >
                    <Icon size={16} className={iconClass} />
                </div>
            </div>
            <p className="text-2xl font-bold text-slate-900 tracking-tight">
                {value}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        </div>
    );
}

interface ConfigCardProps {
    icon: typeof Users;
    title: string;
    children: React.ReactNode;
    className?: string;
}

function ConfigCard({
    icon: Icon,
    title,
    children,
    className = "",
}: ConfigCardProps) {
    return (
        <div
            className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 ${className}`}
        >
            <div className="flex items-center gap-2 mb-3">
                <Icon size={15} className="text-slate-400" />
                <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function EmptyChart() {
    return (
        <div className="h-64 flex flex-col items-center justify-center bg-slate-50 rounded-xl">
            <Activity size={28} className="text-slate-300 mb-2" />
            <p className="text-sm text-slate-500 font-medium">
                No hay datos de performance todavía
            </p>
            <p className="text-xs text-slate-400 mt-1">
                Los números van a aparecer cuando se envíe la primera campaña
            </p>
        </div>
    );
}

/* ─── H11 Sub-components ─── */

function ChannelChip({ channel }: { channel: CampaignChannel }) {
    const map: Record<
        CampaignChannel,
        { label: string; Icon: typeof Mail; color: string }
    > = {
        email: {
            label: "Email",
            Icon: Mail,
            color: "bg-sky-50 text-sky-700 border-sky-200",
        },
        whatsapp: {
            label: "WhatsApp",
            Icon: MessageCircle,
            color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        },
        push: {
            label: "Push",
            Icon: Bell,
            color: "bg-violet-50 text-violet-700 border-violet-200",
        },
    };
    const m = map[channel];
    if (!m) return null;
    const { label, Icon, color } = m;
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${color}`}
        >
            <Icon size={12} />
            {label}
        </span>
    );
}

function TriggerRow({ trigger }: { trigger: Trigger }) {
    if (trigger.type === "event") {
        const def = EVENT_CATALOG.find((e) => e.key === trigger.eventKey);
        return (
            <div className="flex items-start gap-2.5 p-2.5 bg-slate-50 rounded-xl">
                <span className="text-base leading-none flex-shrink-0 mt-0.5">
                    {def?.emoji || "⚡"}
                </span>
                <div>
                    <p className="text-xs font-semibold text-slate-800">
                        {def?.label || trigger.eventKey}
                    </p>
                    {def?.description && (
                        <p className="text-[11px] text-slate-400 mt-0.5">
                            {def.description}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (trigger.type === "time") {
        const modeLabel: Record<string, string> = {
            fixed: "Fecha fija",
            recurring: "Recurrente",
            relative: "Relativo al evento",
        };
        return (
            <div className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-xl">
                <Clock size={14} className="text-slate-400 flex-shrink-0" />
                <div>
                    <p className="text-xs font-semibold text-slate-800">
                        {modeLabel[trigger.mode] || trigger.mode}
                    </p>
                    {trigger.timeOfDay && (
                        <p className="text-[11px] text-slate-400">
                            {trigger.daysOffset != null
                                ? `+${trigger.daysOffset} días · `
                                : ""}
                            {trigger.timeOfDay}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (trigger.type === "whatsapp_message") {
        return (
            <div className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-xl">
                <MessageCircle
                    size={14}
                    className="text-emerald-500 flex-shrink-0"
                />
                <div>
                    <p className="text-xs font-semibold text-slate-800">
                        Mensaje WhatsApp ·{" "}
                        <span className="capitalize">{trigger.category}</span>
                    </p>
                    {trigger.keywords?.length ? (
                        <p className="text-[11px] text-slate-400">
                            {trigger.keywords.join(", ")}
                        </p>
                    ) : null}
                </div>
            </div>
        );
    }

    return null;
}

function AIObservationRow({ obs }: { obs: AIObservation }) {
    const severityMap: Record<
        string,
        { bg: string; text: string; border: string; icon: string }
    > = {
        info: {
            bg: "bg-blue-50",
            text: "text-blue-700",
            border: "border-blue-100",
            icon: "💡",
        },
        warning: {
            bg: "bg-amber-50",
            text: "text-amber-700",
            border: "border-amber-100",
            icon: "⚠️",
        },
        critical: {
            bg: "bg-red-50",
            text: "text-red-700",
            border: "border-red-100",
            icon: "🚫",
        },
    };
    const s = severityMap[obs.severity] ?? severityMap.info;
    return (
        <div className={`p-3 rounded-xl border ${s.bg} ${s.border}`}>
            <div className="flex items-start gap-2">
                <span className="text-sm leading-none flex-shrink-0 mt-0.5">
                    {s.icon}
                </span>
                <div className="flex-1">
                    <p className={`text-xs font-semibold ${s.text}`}>
                        {obs.message}
                    </p>
                    {obs.suggestion && (
                        <p className="text-[11px] text-slate-500 mt-1">
                            → {obs.suggestion}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ObjectiveDashboardPage;
